import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import StrokeAnimation from './StrokeAnimation';

/**
 * FlashcardFront — Inkwell editorial card front.
 * StrokeAnimation is a fixed-pixel SVG, so the per-character size has to be
 * computed in JS. Measure the actual container width (not viewport) so the
 * calc stays correct under any parent layout / padding scheme.
 */
function FlashcardFront({ word }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const measure = () => {
      const w = containerRef.current?.clientWidth ?? 0;
      setContainerWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Resize listener as a safety net for older browsers without ResizeObserver.
  useEffect(() => {
    const onResize = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!word) return null;
  const { hanzi, pinyin } = word;
  const len = (hanzi || '').length;

  // Desktop ceiling per-character — original tier table.
  const desktopMax =
    len >= 8 ? 60 :
    len >= 6 ? 80 :
    len >= 5 ? 100 :
    len >= 4 ? 130 :
    len >= 3 ? 170 :
    len >= 2 ? 230 :
    300;

  // Responsive horizontal padding — a measured container width below 360px
  // means we're on a phone, so trim padding to keep more room for glyphs.
  const isNarrow = containerWidth > 0 && containerWidth < 480;
  const horizontalPadding = isNarrow ? 16 : 48;
  // Inner width = container minus our own padding. Fall back to a reasonable
  // default before the first measurement so the SSR/initial render is sane.
  const innerWidth = containerWidth > 0
    ? containerWidth - horizontalPadding * 2
    : 600;
  // Each glyph plus ~8px inter-glyph gap.
  const sizeFromBudget = Math.floor(innerWidth / Math.max(len, 1)) - 8;
  const strokeSize = Math.max(48, Math.min(sizeFromBudget, desktopMax));

  return (
    <div
      ref={containerRef}
      className="flashcard-front"
      style={{
        padding: `48px ${horizontalPadding}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 480,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <StrokeAnimation
          character={hanzi}
          autoAnimate={false}
          size={strokeSize}
          strokeColor="#14110d"
          outlineColor="#d9d3c4"
          className="front-stroke-animation"
        />
      </div>
      {pinyin && (
        <div
          className="pinyin"
          style={{
            fontSize: len >= 4 ? 22 : 28,
            marginTop: 16,
            marginBottom: 12,
            textAlign: 'center',
            maxWidth: '100%',
          }}
        >
          {pinyin}
        </div>
      )}
      <div className="t-small" style={{ marginTop: 16, textAlign: 'center' }}>
        Tap card to reveal meaning
      </div>
    </div>
  );
}

export default FlashcardFront;
